import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Col } from 'components/grid';

import Toggle from 'components/checkbox';
import Dropdown from 'components/dropdown';
import ToolTip from 'components/tooltip';
import Badge from 'components/badge';

import { reorder } from 'utils/arrayUtils';
import { sortOptions } from 'utils/arraysLists';

import {
  StyledCol,
  StyledColField,
  StyledHeader,
  DraggableListStyled,
} from './styles';

const MAX_AGGREGATORS = 2;

const ColumnsList = ({ columns, setColumns, onSelect }) => {
  const { t } = useTranslation();

  const Header = () => (
    <StyledHeader>
      <StyledColField col={6}>
        {t('pages.searchVehiclePage.fields')}
      </StyledColField>
      <StyledCol col={2}>
        {t('pages.searchVehiclePage.resultsTab.aggregator')}
      </StyledCol>
      <StyledCol col={2}>
        {t('pages.searchVehiclePage.order')}
      </StyledCol>
      <Col col={2} />
    </StyledHeader>
  );

  const onDragEnd = (result) => {
    if (!result.destination) {
      return;
    }

    const items = reorder(
      columns,
      result.source.index,
      result.destination.index,
    );

    const orderedItems = items.map((item, index) => ({ ...item, order: index + 1 }));

    setColumns(orderedItems);
  };

  const getSortOption = (sortBy) => sortOptions.find((option) => option.value === sortBy);

  const setColumn = (newValue) => {
    setColumns(columns.map((item) => (item.field === newValue.field ? newValue : item)));
  };

  const disableAgreggatorTogglesOnMax = columns
    .filter((column) => column.aggregator && !column.disableAggregator).length >= MAX_AGGREGATORS;

  const ColumnsContent = (props) => {
    const { item } = props;
    const {
      name, aggregator, disableAggregator, sortBy, inSearchFilter,
    } = item;

    const disableToggle = !aggregator && disableAgreggatorTogglesOnMax;

    const renderAggregatorToggle = () => !disableAggregator
      && (
        <ToolTip text={t('pages.searchReferencePage.tooltipMaxAggregators')} disabled={!disableToggle}>
          <Toggle
            checked={aggregator}
            disabled={disableToggle}
            onChange={(value) => setColumn({ ...item, aggregator: value })}
          />
        </ToolTip>
      );

    return (
      <>
        <StyledColField col={6}>
          {name}
          {inSearchFilter && <Badge text="filtro" />}
        </StyledColField>
        <StyledCol col={2}>
          {renderAggregatorToggle()}
        </StyledCol>
        <StyledCol col={2}>
          <Dropdown
            value={getSortOption(sortBy)}
            options={sortOptions}
            onChange={(change) => setColumn({ ...item, sortBy: change.value })}
          />
        </StyledCol>
      </>
    );
  };

  ColumnsContent.propTypes = {
    item: PropTypes.shape({
      name: PropTypes.string.isRequired,
      aggregator: PropTypes.bool.isRequired,
      disableAggregator: PropTypes.bool.isRequired,
      inSearchFilter: PropTypes.bool.isRequired,
      sortBy: PropTypes.string.isRequired,
    }).isRequired,
  };

  return (
    <DraggableListStyled
      id="columnsList"
      items={columns}
      Header={Header}
      Content={ColumnsContent}
      selectLabel={t('common.remove')}
      onSelect={onSelect}
      onDragEnd={onDragEnd}
    />
  );
};

ColumnsList.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)).isRequired,
  setColumns: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
};

export default ColumnsList;
